import * as Form from '@radix-ui/react-form'
import { Button } from 'antd'
import useMessage from 'antd/es/message/useMessage'
import { useRolodexEdit } from 'features/fde'
import { generateEntrySlug } from 'features/rolodex'
import { BaseSyntheticEvent, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoComponent,
  GeckoUnit,
  RolodexEntryPutJson,
  RolodexEntryTagsType,
} from 'types'
import { v4 as uuid4 } from 'uuid'

interface EditGeckoComponentInterface {
  unit: GeckoUnit
  refreshData: () => void
  returnToSelectComponent: () => void
  component?: GeckoComponent
}

const StyledFormControl = styled(Form.Control)`
  background-color: ${({ theme }) => theme.forecast.background1} !important;
  color: ${({ theme }) => theme.forecast.text1} !important;
  border-color: ${({ theme }) => theme.forecast.border} !important;
`

const StyledHeaderDiv = styled.div`
  padding-bottom: 0.938rem;
`

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 0.625rem;
`

const StyledSubmitButton = styled(Button)`
  width: 100%;
`

const newComponentEntry = {
  id: 'test',
  type: 'component',
  data: {},
  tags: {},
} as RolodexEntryPutJson

export const EditGeckoComponent = ({
  unit,
  refreshData,
  returnToSelectComponent,
  component,
}: EditGeckoComponentInterface) => {
  const { rolodexPut } = useRolodexEdit()
  const [messageApi, contextHolder] = useMessage()

  const [componentSlug, setComponentSlug] = useState<string>('')
  const [componentName, setComponentName] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const componentHash = useMemo(() => uuid4().slice(0, 6), [])

  const updateSlug = (e: BaseSyntheticEvent) => {
    if (!component) {
      setComponentName(e.target.value)
      setComponentSlug(
        e.target.value ? generateEntrySlug(e.target.value, componentHash) : ''
      )
    }
  }

  useEffect(() => {
    if (component) {
      setComponentName(component.data.name)
      setComponentSlug(component.id)
    }
  }, [component])

  const handleSubmit = (event: BaseSyntheticEvent) => {
    setIsLoading(true)
    const formData = Object.fromEntries(new FormData(event.currentTarget))
    const componentRawJSON = component ? component.raw : newComponentEntry

    if (component) {
      componentRawJSON.data.name = formData.componentName as string
    } else {
      componentRawJSON.id = componentSlug
      componentRawJSON.data.name = formData.componentName as string
      componentRawJSON.tags = {
        unit: unit.id,
        site: unit.tags.site,
        organization: unit.tags.organization,
      } as RolodexEntryTagsType
    }

    rolodexPut([componentRawJSON])
      .then(() => {
        refreshData()
        return messageApi.success(
          `Successfully ${component ? 'modified' : 'added'} component! Loading...`
        )
      })
      .then(() => {
        setIsLoading(false)
        returnToSelectComponent()
      })
      .catch(() => {
        messageApi.error(
          'Unable to modify component. Please check permissions and try again.'
        )
      })

    event.preventDefault()
  }
  return (
    <>
      {contextHolder}
      <StyledHeaderDiv>
        <h3>
          {component ? `Editing: ` : `Adding: `}
          {componentName}
        </h3>
        <h4>
          Slug: <span data-testid='component-slug'>{componentSlug}</span>
        </h4>
      </StyledHeaderDiv>
      <Form.Root onSubmit={handleSubmit}>
        <Form.Field name={'componentName'} key={'componentName'}>
          <Form.Label>Component Name</Form.Label>
          <Form.Message match={'valueMissing'}>
            Component name cannot be blank!
          </Form.Message>
          <StyledFormControl
            defaultValue={component ? component.data.name : undefined}
            onChange={updateSlug}
          />
        </Form.Field>
        <StyledButtonContainer>
          <StyledSubmitButton htmlType={'submit'} loading={isLoading}>
            {component ? 'Edit Component' : 'Create New Component'}
          </StyledSubmitButton>
        </StyledButtonContainer>
      </Form.Root>
    </>
  )
}
